import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  ArrowLeft, Check, Sparkles, HardHat, Clock, Package, TrendingUp, Shield,
  Smartphone, Users, Rocket, MapPin
} from 'lucide-react';
import useProStatus from '../../hooks/useProStatus';
import { toast } from 'sonner';

import { apiFetch } from '../../services/api';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Öffentliche Upgrade-Landingpage für SmartVolt Pro.
 * Über den Inhaber-Dashboard-Button erreichbar unter /pro.
 * Digistore24-Abo-URL wird als Platzhalter vorbereitet und
 * kann später mit der echten Produkt-ID gefüllt werden.
 */

const DIGISTORE_PRO_URL = process.env.REACT_APP_DIGISTORE_PRO_URL
  || 'https://www.digistore24.com/product/PRO_PLACEHOLDER';

const features = [
  {
    icon: HardHat,
    title: 'Mobiles Baustellenmanagement',
    desc: 'Jede Baustelle mit Budget, Adresse, Team-Zuordnung und Live-Fortschritt – dein Team pflegt Daten direkt vom Handy.' },
  {
    icon: Clock,
    title: 'Kolonnen-Zeiterfassung',
    desc: 'Ein Monteur bucht Stunden für die gesamte Kolonne in einem Tap. Automatischer Soll-Ist-Abgleich pro Projekt.' },
  {
    icon: Package,
    title: 'Digitale Materialbeschaffung',
    desc: 'Monteur scannt oder sucht Artikel, legt in den Baustellen-Warenkorb, Chef gibt frei – transparent und lückenlos.' },
  {
    icon: TrendingUp,
    title: 'Chef-Live-Dashboard',
    desc: 'Wer ist wo eingestempelt? Welche Warenkörbe warten auf Freigabe? Krankheitsquote heute? – alles auf einen Blick.' },
  {
    icon: Smartphone,
    title: 'App-Feeling im Browser',
    desc: 'Volle Mobile-App-Optik, keine Installation. Funktioniert auch offline für Baustellen ohne Empfang (in Vorbereitung).' },
  {
    icon: Shield,
    title: 'DSGVO & Made in Germany',
    desc: 'Alle Daten liegen in EU-Rechenzentren. Kein Datenverkauf, keine US-Cloud-Abhängigkeit.' },
];

const bullets = [
  'Unbegrenzte Baustellen & Projekte',
  'Kolonnen-Zeiterfassung für dein gesamtes Team',
  'Material-Freigabe-Workflow mit Genehmigungs-Kette',
  'Chef-Dashboard mit Live-KPIs & Betriebs-Ampel',
  'Krankmeldungen & Anwesenheit tracken',
  'Alle Schulungen aus SmartVolt Academy inklusive',
];

const SmartVoltProLandingPage = () => {
  const navigate = useNavigate();
  const pro = useProStatus();
  const [starting, setStarting] = useState(false);

  const startCheckout = () => {
    // Öffnet Digistore-Checkout in neuem Tab
    window.open(DIGISTORE_PRO_URL, '_blank', 'noopener,noreferrer');
  };

  const activateTrial = async () => {
    setStarting(true);
    try {
      const res = await apiFetch(`${API}/api/pro/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: 'pro-trial', seats: 10 }) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Aktivierung fehlgeschlagen');
      toast.success('SmartVolt Pro Trial aktiviert', {
        description: 'Du kannst jetzt das neue Chef-Dashboard und die Mobile-App testen.' });
      pro.reload();
      setTimeout(() => navigate('/pro/dashboard'), 900);
    } catch (err) {
      toast.error(err.message || 'Fehler');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black text-white">
      <Helmet>
        <title>SmartVolt Pro – Baustellen-, Zeit- & Material-Management</title>
        <meta name="description" content="Der digitale Handwerksbetrieb: Baustellen, Zeiterfassung, Material – alles in einer App. Von SmartVolt Academy." />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-yellow-500/20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-300 hover:text-yellow-500 hover:bg-yellow-500/10"
            data-testid="pro-landing-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
          </Button>
          <span className="text-yellow-500 font-bold tracking-wider">SmartVolt <span className="text-white">Pro</span></span>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-16" data-testid="smartvolt-pro-landing">
        {/* Hero */}
        <section className="text-center space-y-5">
          <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 text-xs font-semibold uppercase tracking-widest">
            <Sparkles className="h-3 w-3" /> Ganz neu · Ab sofort verfügbar
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
            Bring deinen Betrieb <br />
            <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-500 bg-clip-text text-transparent">
              auf das nächste Level
            </span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            SmartVolt Pro digitalisiert deinen Handwerksbetrieb – Baustellen, Zeit, Material und Team-Führung
            in <strong className="text-white">einer</strong> App, die dein Team schon kennt.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 pt-4">
            {pro.active ? (
              <Button
                size="lg"
                onClick={() => navigate('/pro/dashboard')}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-black font-bold px-8 py-6"
                data-testid="pro-goto-dashboard"
              >
                Zum Pro-Dashboard <Rocket className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <>
                <Button
                  size="lg"
                  onClick={startCheckout}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-bold px-8 py-6 shadow-lg"
                  data-testid="pro-start-checkout"
                >
                  <Rocket className="h-4 w-4 mr-2" /> Jetzt Pro abonnieren
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={activateTrial}
                  disabled={starting}
                  className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 px-8 py-6"
                  data-testid="pro-activate-trial"
                >
                  {starting ? 'Aktiviere…' : '14 Tage kostenlos testen'}
                </Button>
              </>
            )}
          </div>
          <p className="text-xs text-gray-500">Monatlich kündbar · Alle Schulungen der SmartVolt Academy inklusive</p>
        </section>

        {/* Features Grid */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <Card key={f.title} className="bg-gray-950/60 border-gray-800 hover:border-yellow-500/40 transition-colors">
              <CardContent className="p-6">
                <div className="w-11 h-11 rounded-xl bg-yellow-500/15 border border-yellow-500/30 flex items-center justify-center mb-3">
                  <f.icon className="h-5 w-5 text-yellow-400" />
                </div>
                <h3 className="text-white font-semibold text-lg">{f.title}</h3>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        {/* Value Bullets */}
        <section className="bg-gradient-to-br from-yellow-500/5 to-orange-500/5 border border-yellow-500/20 rounded-2xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Users className="h-6 w-6 text-yellow-500" /> Was du bekommst
              </h2>
              <ul className="space-y-2">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-gray-200">
                    <Check className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" /> {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-black/40 border border-gray-800 p-6 text-center flex flex-col justify-center">
              <p className="text-gray-400 text-sm">SmartVolt Pro pro Nutzer</p>
              <p className="text-5xl font-black text-white mt-2">
                29<span className="text-2xl align-top">,00 €</span>
              </p>
              <p className="text-gray-500 text-xs">pro Monat / Nutzer · zzgl. USt.</p>
              <p className="text-yellow-400 text-sm mt-3">Alles aus SmartVolt Academy inklusive</p>
              <Button
                size="lg"
                onClick={pro.active ? () => navigate('/pro/dashboard') : startCheckout}
                className="mt-5 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-bold"
                data-testid="pro-price-cta"
              >
                {pro.active ? 'Zum Pro-Dashboard' : 'Jetzt starten'}
              </Button>
            </div>
          </div>
        </section>

        {/* Beispiel-Screens Placeholder */}
        <section className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Ein Betrieb, drei Rollen, eine App</h2>
          <p className="text-gray-400 mb-6">Chef · Monteur · Büro – jeder sieht genau das, was er braucht.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['Chef-Cockpit', 'Monteur-App', 'Material-Freigabe'].map((title, i) => (
              <div key={title} className="aspect-[9/16] max-h-96 mx-auto w-full max-w-56 bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 rounded-3xl flex flex-col items-center justify-center gap-3">
                {i === 0 && <MapPin className="h-10 w-10 text-yellow-400" />}
                {i === 1 && <Smartphone className="h-10 w-10 text-yellow-400" />}
                {i === 2 && <Package className="h-10 w-10 text-yellow-400" />}
                <p className="text-white font-semibold">{title}</p>
                <p className="text-xs text-gray-500 px-4 text-center">Mobile-First UI · Live-Preview folgt</p>
              </div>
            ))}
          </div>
        </section>

        <footer className="pt-8 pb-4 text-center text-xs text-gray-600 border-t border-gray-800">
          Fragen? Schreib uns an <a href="mailto:smartvolt-academy@gmx.net" className="text-yellow-500 hover:underline">smartvolt-academy@gmx.net</a>
        </footer>
      </main>
    </div>
  );
};

export default SmartVoltProLandingPage;
